import {
	CalendarPlus01,
	ChartBreakoutSquare,
	CheckCircle,
	MessageChatCircle,
	PlayCircle,
	ZapFast,
	CreditCardRefresh,
	File05,
	Heart,
	Mail01,
	SlashCircle01,
	SwitchHorizontal01,
} from '@untitledui/icons'
import { AnimatePresence, type Transition, motion } from 'motion/react'
import { type ReactNode, Fragment, useEffect, useState } from 'react'
import { Collection, Tab, TabList, TabPanel, Tabs } from 'react-aria-components'
import { redirect } from 'react-router'
import { PaginationDot } from '#app/components/application/pagination/pagination-dot.tsx'
import { Avatar } from '#app/components/base/avatar/avatar.tsx'
import { Button } from '#app/components/base/buttons/button.tsx'
import { FeaturedIcon } from '#app/components/foundations/featured-icon/featured-icon.tsx'
import { CONSTANTS, DASHBOARD_PATH } from '#app/constants/index.ts'
import featureCampaign from '#app/images/features/campaign.png'
import featureRequirements from '#app/images/features/requirements.png'
import featureShortlist from '#app/images/features/shortlist.png'
import glogoChem from '#app/images/glogo/glogo_chem.png'
import glogoElis from '#app/images/glogo/glogo_elis.png'
import glogoFwr from '#app/images/glogo/glogo_fwr.png'
import glogoGa from '#app/images/glogo/glogo_ga.png'
import glogoLeva from '#app/images/glogo/glogo_leva.png'
import glogoLiftt from '#app/images/glogo/glogo_liftt.png'
import glogoPnp from '#app/images/glogo/glogo_pnp.png'
import glogoTechstars from '#app/images/glogo/glogo_techstars.png'
import glogoTutornow from '#app/images/glogo/glogo_tutornow.png'
import avatarIvan from '#app/images/testimonials/chem_ivan.jpg'
import avatarLiam from '#app/images/testimonials/gad_liam.jpeg'
import avatarMatteo from '#app/images/testimonials/leva_matteo.jpeg'
import { getUserId } from '#app/utils/auth.server.ts'
import { cx } from '#app/utils/cx.ts'
import { getHostMode, getHostOrigins } from '#app/utils/hosts.server.ts'
import { useAppHref, useHostOrigins } from '#app/utils/hosts.ts'
import { createMeta } from '#app/utils/meta.ts'
import { useOptionalUser } from '#app/utils/user.ts'
import { type Route } from './+types/index.ts'

export const meta: Route.MetaFunction = ({ matches }) => {
	return createMeta(matches, { title: 'Home' })
}

export async function loader({ request }: Route.LoaderArgs) {
	// This homepage belongs to the marketing host. On the application host `/` is
	// not a page: signed-in recruiters go to their dashboard, and everyone else
	// goes to the marketing site, where this page now lives.
	if (getHostMode(request) === 'app') {
		const userId = await getUserId(request)
		throw redirect(userId ? DASHBOARD_PATH : getHostOrigins(request).marketing)
	}
	return null
}

const HeroScreenMockup01 = ({ betaAccessHref }: { betaAccessHref: string }) => {
	return (
		<div className="relative overflow-hidden">
			<section className="relative overflow-hidden py-16 md:py-24">
				<div className="max-w-container mx-auto w-full px-4 md:px-8">
					<div className="mx-auto flex max-w-5xl flex-col items-center text-center">
						<p className="border-secondary bg-primary_alt text-secondary mb-4 inline-flex items-center gap-2 rounded-full border py-1 pr-3 pl-2 text-sm font-medium md:mb-6">
							<span
								aria-hidden="true"
								className="bg-fg-brand-primary size-2 shrink-0 rounded-full"
							/>
							{CONSTANTS.RECRUITER_PRODUCT_NAME} is now in beta
						</p>
						<h1 className="text-display-md text-primary md:text-display-lg lg:text-display-xl font-semibold text-balance">
							Turn a job description into a ranked candidate shortlist-in
							minutes
						</h1>
						<p className="text-tertiary mt-4 max-w-3xl text-lg md:mt-6 md:text-xl">
							Spacebrains tackles the biggest problem in hiring today: deciding
							who is worth talking to, without spending hours sourcing
							candidates or sorting through piles of AI generated applications.
						</p>
						<div className="mt-8 flex w-full flex-col-reverse items-stretch gap-3 sm:w-auto sm:flex-row sm:items-start md:mt-12">
							<Button size="xl" href={betaAccessHref}>
								Get beta access
							</Button>
							<Button
								color="secondary"
								size="xl"
								href="https://calendly.com/spacebrains/intro-call-en"
								target="_blank"
								rel="noopener noreferrer"
							>
								Book a demo
							</Button>
						</div>
					</div>
				</div>
			</section>
		</div>
	)
}

const logos = [
	{ name: 'Techstars', imageUrl: glogoTechstars },
	{ name: 'Leva Engineering', imageUrl: glogoLeva },
	{ name: 'LIFTT', imageUrl: glogoLiftt },
	{ name: 'GA Drilling', imageUrl: glogoGa },
	{ name: 'CHEMPROCES', imageUrl: glogoChem },
	{ name: 'Plug&Play', imageUrl: glogoPnp },
	{ name: 'Fluid Wire Robotics', imageUrl: glogoFwr },
	{ name: 'ELISTECH', imageUrl: glogoElis },
	{ name: 'Tutornow', imageUrl: glogoTutornow },
]

const SocialProofFullWidthMasked = () => {
	return (
		<section className="bg-primary_alt overflow-hidden py-16 md:py-24">
			<div className="max-w-container mx-auto px-4 md:px-8">
				<div className="flex flex-col gap-8">
					<div className="flex max-w-full flex-col items-center gap-y-4 mask-x-from-80%">
						{/* Top layer of logos (visible on all viewports) */}
						<div className="flex">
							<div className="animate-marquee flex w-auto max-w-none shrink-0 justify-center gap-5 pl-5 motion-reduce:animate-none md:gap-6 md:pl-6">
								{logos.map((logo) => (
									<img
										key={logo.name}
										alt={logo.name}
										src={logo.imageUrl}
										className="h-16 md:h-24"
									/>
								))}
							</div>

							<div className="animate-marquee flex w-auto max-w-none shrink-0 justify-center gap-5 pl-5 motion-reduce:animate-none md:gap-6 md:pl-6">
								{logos.map((logo) => (
									<img
										key={logo.name}
										alt={logo.name}
										src={logo.imageUrl}
										className="h-16 md:h-24"
									/>
								))}
							</div>
						</div>
					</div>
				</div>
			</div>
		</section>
	)
}

const FeaturesAlternatingLayout01 = () => {
	return (
		<section className="bg-primary flex flex-col gap-12 overflow-hidden py-16 sm:gap-16 md:gap-20 md:py-24 lg:gap-24">
			<div className="max-w-container mx-auto w-full px-4 md:px-8">
				<div className="mx-auto flex w-full max-w-3xl flex-col items-center text-center">
					<h2 className="text-display-sm text-primary md:text-display-md mt-3 font-semibold text-balance">
						Source, evaluate and choose candidates in one workflow
					</h2>
					<p className="text-tertiary mt-4 text-lg md:mt-5 md:text-xl">
						Spacebrains takes your job input, finds relevant candidates,
						evaluates fit against your requirements, and returns a ranked
						shortlist with reasoning.
					</p>
				</div>
			</div>

			<div className="max-w-container mx-auto flex w-full flex-col gap-12 px-4 sm:gap-16 md:gap-20 md:px-8 lg:gap-24">
				<div className="grid grid-cols-1 items-center gap-10 md:gap-16 lg:grid-cols-2 lg:gap-24">
					<div className="mx-auto flex max-w-xl flex-col items-center text-center lg:items-end lg:justify-self-end lg:text-right">
						<FeaturedIcon
							icon={MessageChatCircle}
							size="lg"
							color="brand"
							theme="light"
						/>
						<h2 className="text-display-xs text-primary md:text-display-sm mt-5 font-semibold">
							Add your job input
						</h2>
						<p className="text-md text-tertiary mt-2 text-balance md:mt-4 md:text-lg">
							Paste a job description, recruiter notes, client call notes, or
							just a short prompt.
						</p>
					</div>
					<div className="flex justify-center lg:justify-start">
						<img
							alt="Campaign creation interface"
							src={featureCampaign}
							className="w-full max-w-sm rounded-xl"
						/>
					</div>
				</div>

				<div className="grid grid-cols-1 items-center gap-10 md:gap-16 lg:grid-cols-2 lg:gap-24">
					<div className="mx-auto flex max-w-xl flex-col items-center text-center lg:order-last lg:items-start lg:text-left">
						<FeaturedIcon
							icon={ZapFast}
							size="lg"
							color="brand"
							theme="light"
						/>
						<h2 className="text-display-xs text-primary md:text-display-sm mt-5 font-semibold">
							Review job requirements
						</h2>
						<p className="text-md text-tertiary mt-2 text-balance md:mt-4 md:text-lg">
							The system turns messy input into clear requirements you can
							review and edit before evaluation runs. Full AI transparency in
							your control.
						</p>
					</div>
					<div className="flex justify-center lg:justify-end">
						<img
							alt="Requirements review interface"
							src={featureRequirements}
							className="w-full max-w-sm rounded-xl"
						/>
					</div>
				</div>

				<div className="grid grid-cols-1 items-center gap-10 md:gap-16 lg:grid-cols-2 lg:gap-24">
					<div className="mx-auto flex max-w-xl flex-col items-center text-center lg:items-end lg:justify-self-end lg:text-right">
						<FeaturedIcon
							icon={ChartBreakoutSquare}
							size="lg"
							color="brand"
							theme="light"
						/>
						<h2 className="text-display-xs text-primary md:text-display-sm mt-5 font-semibold">
							Instant ranked shortlist.
						</h2>
						<p className="text-md text-tertiary mt-2 text-balance md:mt-4 md:text-lg">
							Browse, filter an see who matches, why they match, and where the
							gaps are.
						</p>
					</div>
					<div className="flex justify-center lg:justify-start">
						<img
							alt="Ranked shortlist interface"
							src={featureShortlist}
							className="w-full max-w-sm rounded-xl"
						/>
					</div>
				</div>
			</div>
		</section>
	)
}

const CTACardHorizontal = ({ betaAccessHref }: { betaAccessHref: string }) => {
	return (
		<section className="bg-primary py-16 md:py-24">
			<div className="max-w-container mx-auto px-4 md:px-8">
				<div className="bg-secondary flex flex-col gap-x-8 gap-y-2 rounded-2xl px-6 py-10 lg:p-16">
					<div className="flex flex-col gap-x-8 gap-y-8 lg:flex-row lg:items-center">
						<div className="flex max-w-3xl flex-1 flex-col">
							<p className="text-tertiary mt-4 text-xl font-semibold md:mt-5 lg:text-2xl">
								Built for recruiters who are tired of manual sourcing,
								<br />
								noisy profiles, and black box tools.
							</p>
						</div>
						<div className="flex flex-col-reverse items-stretch gap-3 sm:flex-row sm:items-start">
							<Button size="xl" href={betaAccessHref}>
								Get beta access *
							</Button>
							<Button
								color="secondary"
								size="xl"
								href="https://calendly.com/spacebrains/intro-call-en"
								target="_blank"
								rel="noopener noreferrer"
							>
								Book a demo
							</Button>
						</div>
					</div>
					<p className="text-tertiary mt-4 text-sm md:mt-5">
						* free beta ending soon
					</p>
				</div>
			</div>
		</section>
	)
}

const reviews = [
	{
		id: 'review-01',
		quote:
			'In the first week, Spacebrains sent us stronger candidates than any agency ever had - so we gave them four more roles. Their peer vetting gives us confidence in every hire.',
		author: {
			name: 'Matteo Mosca',
			title: 'CEO, Leva',
			avatarUrl: avatarMatteo,
		},
	},
	{
		id: 'review-02',
		quote:
			"Expanding across Europe, the US, and Brazil was hard. Spacebrains halved our screening time and surfaced talent we wouldn't have found on our own.",
		author: {
			name: 'Liam Lines',
			title: 'Engineering Director, GA Drilling',
			avatarUrl: avatarLiam,
		},
	},
	{
		id: 'review-03',
		quote:
			'I was skeptical that an external solution could find our perfect fit - until just days after signing, Spacebrains delivered one.',
		author: {
			name: 'Ivan Zakov',
			title: 'Managing Director, Chemproces',
			avatarUrl: avatarIvan,
		},
	},
]

const transition: Transition = {
	type: 'spring',
	duration: 0.8,
}

export const TestimonialSimpleCentered01 = () => {
	const [currentReviewIndex, setCurrentReviewIndex] = useState(0)

	useEffect(() => {
		const interval = setInterval(() => {
			setCurrentReviewIndex((prev) => (prev + 1) % reviews.length)
		}, 5000)
		return () => clearInterval(interval)
	}, [])

	return (
		<section className="bg-primary py-16 md:py-24">
			<div className="max-w-container mx-auto px-4 md:px-8">
				<div className="flex flex-col items-center gap-10">
					<figure className="flex max-w-270 flex-col gap-8 text-center">
						<AnimatePresence initial={false} mode="popLayout">
							<motion.blockquote
								key={currentReviewIndex + '-quote'}
								initial={{
									opacity: 0,
									scale: 0.98,
									y: 20,
								}}
								animate={{
									opacity: 1,
									scale: 1,
									y: 0,
									transition: {
										...transition,
										delay: 0.4,
									},
								}}
								exit={{
									opacity: 0,
									scale: 0.98,
									y: 20,
									transition: {
										...transition,
										delay: 0.06,
									},
								}}
								className="text-display-sm text-primary md:text-display-md origin-bottom font-medium text-balance will-change-transform"
							>
								{reviews[currentReviewIndex]?.quote}
							</motion.blockquote>
							<motion.figcaption
								key={currentReviewIndex + '-author'}
								initial={{
									opacity: 0,
									scale: 0.98,
									y: 20,
								}}
								animate={{
									opacity: 1,
									scale: 1,
									y: 0,
									transition: {
										...transition,
										delay: 0.5,
									},
								}}
								exit={{
									opacity: 0,
									scale: 0.98,
									y: 20,
									transition,
								}}
								className="flex origin-bottom flex-col items-center gap-4 will-change-transform"
							>
								<div className="flex flex-col items-center gap-4">
									<Avatar
										src={reviews[currentReviewIndex]?.author?.avatarUrl}
										alt={reviews[currentReviewIndex]?.author?.name}
										size="2xl"
									/>
									<div className="flex flex-col gap-1">
										<p className="text-primary text-lg font-semibold">
											{reviews[currentReviewIndex]?.author?.name}
										</p>
										<cite className="text-md text-tertiary not-italic">
											{reviews[currentReviewIndex]?.author?.title}
										</cite>
									</div>
								</div>
							</motion.figcaption>
						</AnimatePresence>
					</figure>

					<PaginationDot
						page={currentReviewIndex + 1}
						total={3}
						size="lg"
						onPageChange={(page) => setCurrentReviewIndex(page - 1)}
					/>
				</div>
			</div>
		</section>
	)
}

const CheckItemText = (props: {
	size?: 'sm' | 'md' | 'lg' | 'xl'
	text?: string
	color?: 'primary' | 'success'
	iconStyle?: 'outlined' | 'contained' | 'filled'
	textClassName?: string
}) => {
	const { text, color, size, iconStyle = 'contained' } = props

	return (
		<li className="flex gap-3">
			{iconStyle === 'contained' && (
				<div
					className={cx(
						'flex shrink-0 items-center justify-center rounded-full',
						color === 'success'
							? 'bg-success-secondary text-featured-icon-light-fg-success'
							: 'bg-brand-primary text-featured-icon-light-fg-brand',
						size === 'lg'
							? 'size-7 md:h-8 md:w-8'
							: size === 'md'
								? 'size-7'
								: 'size-6',
					)}
				>
					<svg
						width={size === 'lg' ? 16 : size === 'md' ? 15 : 13}
						height={size === 'lg' ? 14 : size === 'md' ? 13 : 11}
						viewBox="0 0 13 11"
						fill="none"
					>
						<path
							fillRule="evenodd"
							clipRule="evenodd"
							d="M11.0964 0.390037L3.93638 7.30004L2.03638 5.27004C1.68638 4.94004 1.13638 4.92004 0.736381 5.20004C0.346381 5.49004 0.236381 6.00004 0.476381 6.41004L2.72638 10.07C2.94638 10.41 3.32638 10.62 3.75638 10.62C4.16638 10.62 4.55638 10.41 4.77638 10.07C5.13638 9.60004 12.0064 1.41004 12.0064 1.41004C12.9064 0.490037 11.8164 -0.319963 11.0964 0.380037V0.390037Z"
							fill="currentColor"
						/>
					</svg>
				</div>
			)}

			{iconStyle === 'filled' && (
				<div className="bg-brand-solid flex size-6 shrink-0 items-center justify-center rounded-full text-white">
					<svg width="12" height="8" viewBox="0 0 12 8" fill="none">
						<path
							d="M1.5 4L4.5 7L10.5 1"
							stroke="currentColor"
							strokeWidth="2"
							strokeLinecap="round"
							strokeLinejoin="round"
						/>
					</svg>
				</div>
			)}

			{iconStyle === 'outlined' && (
				<CheckCircle
					className={cx(
						'shrink-0',
						color === 'success'
							? 'text-fg-success-primary'
							: 'text-fg-brand-primary',
						size === 'lg'
							? 'size-7 md:h-8 md:w-8'
							: size === 'md'
								? 'size-7'
								: 'size-6',
					)}
				/>
			)}

			<span
				className={cx(
					'text-tertiary',
					size === 'lg'
						? 'pt-0.5 text-lg md:pt-0'
						: size === 'md'
							? 'text-md pt-0.5 md:pt-0 md:text-lg'
							: 'text-md',
					iconStyle === 'filled' && 'text-brand-secondary',
					props.textClassName,
				)}
			>
				{text}
			</span>
		</li>
	)
}

type Faq = {
	question: string
	/** Rich content is allowed so answers can link to the other Spacebrains sites. */
	answer: ReactNode
	icon: typeof Heart
}

const getFaqs = (agencyOrigin: string): Faq[] => [
	{
		question: 'How is Spacebrains different from L*nkedIn Recruiter?',
		answer:
			'L*nkedIn Recruiter is mainly a search tool. You set filters and get a list of profiles, but recruiters still have to review them manually to decide who is worth attention. Too narrow, and you miss good people. Too broad, and you get hundreds of profiles with little prioritization. Spacebrains solves the full sourcing loop by evaluating and ranking candidates against the role, so you move from job description to shortlist in minutes.',
		icon: Heart,
	},
	{
		question: 'Is Spacebrains a replacement for our ATS?',
		answer:
			'No. Spacebrains helps with sourcing and evaluation before the ATS becomes useful. It fits into your workflow. We are also happy to explore integration into your ATS if needed.',
		icon: SwitchHorizontal01,
	},
	{
		question: 'Does AI make the final hiring decision?',
		answer:
			'No. Recruiters stay in control. Spacebrains helps you evaluate faster and more consistently.',
		icon: SlashCircle01,
	},
	{
		question:
			'Does Spacebrains automatically reach out to shortlisted candidates?',
		answer:
			'Not yet. Automated outreach is a planned feature. We are working on support for LinkedIn, email, and AI assisted outreach workflows.',
		icon: File05,
	},
	{
		question: 'What kind of roles is it best for?',
		answer:
			'Spacebrains works best on roles where shortlist quality matters more than volume. The core technology was built for demanding tech roles, but we are already seeing it used beyond tech as well, across operations, finance, marketing and other specialist and executive hires.',
		icon: CreditCardRefresh,
	},
	{
		question: 'Am I in control of the AI evaluation?',
		answer:
			'Yes. You review and adjust the requirements before the evaluation runs, and you can change them and re-run it afterwards. Spacebrains shows you why each candidate was ranked the way they were, so you can judge the reasoning rather than take a score on trust.',
		icon: Mail01,
	},
	{
		question: 'Do you also offer recruitment as a service?',
		answer: (
			<>
				Yes. If your team does not want to run the process internally,{' '}
				{CONSTANTS.AGENCY_NAME} can run selected searches for you end to end,
				using the same technology.{' '}
				<a
					href={agencyOrigin}
					className="text-brand-secondary font-medium underline"
				>
					Explore how our recruitment agency works
				</a>
				.
			</>
		),
		icon: Heart,
	},
]

export const FAQAccordion02 = () => {
	const { agency } = useHostOrigins()
	const faqsExtended = getFaqs(agency)
	const [openQuestions, setOpenQuestions] = useState(new Set([0]))

	const handleToggle = (index: number) => {
		openQuestions.has(index)
			? openQuestions.delete(index)
			: openQuestions.add(index)
		setOpenQuestions(new Set(openQuestions))
	}

	return (
		<section className="bg-primary py-16 md:py-24">
			<div className="max-w-container mx-auto px-4 md:px-8">
				<div className="mx-auto flex w-full max-w-3xl flex-col items-center text-center">
					<h2 className="text-display-sm text-primary md:text-display-md font-semibold">
						Frequently asked questions
					</h2>
					<p className="text-tertiary mt-4 text-lg md:mt-5 md:text-xl">
						Everything you need to know about {CONSTANTS.RECRUITER_PRODUCT_NAME}
						.
					</p>
				</div>

				<div className="mx-auto mt-12 max-w-3xl md:mt-16">
					<div className="flex flex-col gap-4">
						{faqsExtended.map((faq, index) => (
							<div
								key={faq.question}
								className={cx(
									'rounded-2xl bg-transparent p-5 transition duration-300 ease-in-out md:p-6',
									openQuestions.has(index) && 'bg-secondary',
								)}
							>
								<h3>
									<button
										onClick={() => handleToggle(index)}
										className="outline-focus-ring flex w-full cursor-pointer gap-2 rounded-md text-left select-none focus-visible:outline-2 focus-visible:outline-offset-2 md:flex-row-reverse md:gap-4"
									>
										<span className="text-md text-primary flex-1 font-semibold">
											{faq.question}
										</span>
										<span
											aria-hidden="true"
											className="text-fg-quaternary flex size-6 items-center"
										>
											<svg
												width="24"
												height="24"
												viewBox="0 0 24 24"
												fill="none"
												stroke="currentColor"
												strokeWidth="2"
												strokeLinecap="round"
												strokeLinejoin="round"
											>
												<circle cx="12" cy="12" r="10"></circle>
												<line
													className={cx(
														'origin-center rotate-0 transition duration-150 ease-out',
														openQuestions.has(index) && '-rotate-90',
													)}
													x1="12"
													y1="8"
													x2="12"
													y2="16"
												></line>
												<line x1="8" y1="12" x2="16" y2="12"></line>
											</svg>
										</span>
									</button>
								</h3>

								<motion.div
									className="overflow-hidden"
									initial={false}
									animate={{
										height: openQuestions.has(index) ? 'auto' : 0,
										opacity: openQuestions.has(index) ? 1 : 0,
									}}
									transition={{
										type: 'spring',
										damping: 24,
										stiffness: 240,
										bounce: 0.4,
									}}
								>
									<div className="pt-1 pr-8 md:pr-0 md:pl-10">
										<p className="text-md text-tertiary">{faq.answer}</p>
									</div>
								</motion.div>
							</div>
						))}
					</div>
				</div>
			</div>
		</section>
	)
}

const LandingPage02 = () => {
	const user = useOptionalUser()
	// On the marketing host this resolves to an absolute app URL: the two hosts
	// deliberately do not share session cookies, so `user` is always null there.
	const betaAccessHref = useAppHref(user ? DASHBOARD_PATH : '/signup')

	return (
		<div className="bg-primary">
			<HeroScreenMockup01 betaAccessHref={betaAccessHref} />

			<SocialProofFullWidthMasked />

			<FeaturesAlternatingLayout01 />

			<CTACardHorizontal betaAccessHref={betaAccessHref} />

			<TestimonialSimpleCentered01 />

			<FAQAccordion02 />
		</div>
	)
}

export default LandingPage02
